import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Input, Alert, message } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined, MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { login, verify2FA, resend2FA } from '../services/authService';
import { setCredentials } from '../store/authSlice';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dispatch = useDispatch();

  // 2FA state
  const [step, setStep] = useState('credentials'); // 'credentials' | 'code'
  const [userId, setUserId] = useState(null);
  const [emailHint, setEmailHint] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login({ username, password });
      if (result.requires_2fa) {
        setUserId(result.user_id);
        setEmailHint(result.email_hint || '');
        setStep('code');
        setResendCooldown(60);
        if (!result.email_sent) {
          setError('No se pudo enviar el email. Verifica la configuración SMTP.');
        }
      } else if (result.token) {
        dispatch(setCredentials({ token: result.token, user: result.user || { username } }));
      } else {
        setError('El servidor no devolvió un token. Verifique la configuración.');
      }
    } catch {
      setError('Usuario o contraseña incorrectos. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Ingresa el código de 6 dígitos completo');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { token, user } = await verify2FA({ user_id: userId, code: fullCode });
      if (token) {
        dispatch(setCredentials({ token, user: user || { username } }));
      } else {
        setError('Error al verificar el código.');
      }
    } catch (err) {
      setError(err?.error || 'Código incorrecto o expirado.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const res = await resend2FA(userId);
      if (res.email_sent) {
        message.success('Código reenviado');
      } else {
        message.error('No se pudo enviar el email');
      }
      setResendCooldown(60);
    } catch {
      message.error('Error al reenviar el código');
    }
  };

  const handleBack = () => {
    setStep('credentials');
    setCode(['', '', '', '', '', '']);
    setError('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-brand-icon">
            <img src="/logo.png" alt="SmartAdmin" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          </div>
          <div className="login-title">SmartAdmin</div>
          <div className="login-subtitle">
            {step === 'credentials' ? 'Panel de Control de Servidores' : 'Verificación en Dos Pasos'}
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 20, borderRadius: 8, background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.2)' }}
          />
        )}

        {step === 'credentials' ? (
          <form onSubmit={handleSubmit} className="login-form">
            <Input
              prefix={<UserOutlined style={{ color: '#64748b' }} />}
              placeholder="Usuario"
              value={username}
              onChange={e => setUsername(e.target.value)}
              style={{ marginBottom: 16 }}
              autoFocus
              size="large"
            />
            <Input.Password
              prefix={<LockOutlined style={{ color: '#64748b' }} />}
              placeholder="Contraseña"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ marginBottom: 24 }}
              size="large"
            />
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              size="large"
              style={{ background: 'linear-gradient(135deg, #845adf, #6c3fc5)', border: 'none', borderRadius: 10, height: 48, fontWeight: 600, fontSize: 15 }}
            >
              Iniciar Sesión
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode} className="login-form">
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <MailOutlined style={{ fontSize: 40, color: '#845adf', marginBottom: 12 }} />
              <p style={{ color: '#64748b', margin: 0, fontSize: 14 }}>
                Enviamos un código de 6 dígitos a
              </p>
              <p style={{ color: '#e2e8f0', fontWeight: 600, margin: '4px 0 0' }}>
                {emailHint}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24 }} onPaste={handleCodePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleCodeChange(i, e.target.value)}
                  onKeyDown={e => handleCodeKeyDown(i, e)}
                  autoFocus={i === 0}
                  style={{
                    width: 48, height: 56, textAlign: 'center', fontSize: 24, fontWeight: 700,
                    background: 'rgba(132,90,223,0.1)', border: '2px solid rgba(132,90,223,0.3)',
                    borderRadius: 10, color: '#e2e8f0', outline: 'none', transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = '#845adf'}
                  onBlur={e => e.target.style.borderColor = 'rgba(132,90,223,0.3)'}
                />
              ))}
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              size="large"
              icon={<SafetyOutlined />}
              style={{ background: 'linear-gradient(135deg, #845adf, #6c3fc5)', border: 'none', borderRadius: 10, height: 48, fontWeight: 600, fontSize: 15, marginBottom: 12 }}
            >
              Verificar Código
            </Button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button type="link" icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ color: '#64748b', padding: 0 }}>
                Volver
              </Button>
              <Button
                type="link"
                onClick={handleResend}
                disabled={resendCooldown > 0}
                style={{ color: resendCooldown > 0 ? '#475569' : '#845adf', padding: 0 }}
              >
                {resendCooldown > 0 ? `Reenviar (${resendCooldown}s)` : 'Reenviar código'}
              </Button>
            </div>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: '#64748b' }}>
          Smart & Safe · Server Monitor v2.0
        </div>
      </div>
    </div>
  );
};

export default Login;
